import * as React from 'react';
import { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  Grid,
  Alert,
  IconButton,
  Chip,
  Avatar,
  Button
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  CheckCircle as ApprovedIcon,
  Pending as PendingIcon,
  Cancel as RejectedIcon,
  Person as PersonIcon,
  Category as CategoryIcon,
  Inventory as InventoryIcon,
  AttachMoney as MoneyIcon,
  CalendarToday as CalendarIcon,
  Description as DescriptionIcon,
  Store as SupplierIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  AttachFile as AttachFileIcon,
  InsertDriveFile as FileIcon
} from '@mui/icons-material';
import { colors } from '../../utils/colors';
import { getProcurementDetailsAPI, getProcurementDocumentsAPI } from '../../lib/api';
import LoadingProgress from '../../components/common/LoadingProgress';

const ProcurementDetails = ({ customerId, projectId, procurementId, onBack }) => {
  const [procurement, setProcurement] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [documentsLoading, setDocumentsLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchProcurementDetails();
    fetchProcurementDocuments();
  }, [customerId, projectId, procurementId]);

  const fetchProcurementDetails = async () => {
    try {
      setLoading(true);
      setError('');
      const result = await getProcurementDetailsAPI(customerId, projectId, procurementId);
      if (result.status && result.data) {
        setProcurement(result.data);
      } else {
        setError('Failed to load procurement details');
      }
    } catch (error) {
      setError(error.message || 'Failed to fetch procurement details');
      console.error('Error fetching procurement details:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchProcurementDocuments = async () => {
    try {
      setDocumentsLoading(true);
      const result = await getProcurementDocumentsAPI(customerId, projectId, procurementId);
      if (result.status && result.data) {
        setDocuments(Array.isArray(result.data) ? result.data : []);
      }
    } catch (error) {
      console.error('Error fetching procurement documents:', error);
      setDocuments([]);
    } finally {
      setDocumentsLoading(false);
    }                                                                                                                                                                                                                                                                            
  };

  const formatAmount = (amount) => {
    if (!amount) return 'N/A';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      // case 'approved':
      //   return {
      //     bg: colors.success.light,
      //     text: colors.success.main,
      //     icon: <ApprovedIcon sx={{ fontSize: 20 }} />
      //   };
      case 'pending':
        return {
          bg: colors.warning.light,
          text: colors.warning.main,
          icon: <PendingIcon sx={{ fontSize: 20 }} />
        };
      case 'rejected':
        return {
          bg: colors.error.light,
          text: colors.error.main,
          icon: <RejectedIcon sx={{ fontSize: 20 }} />
        };
      default:
        return {
          bg: colors.gray[100],
          text: colors.text.secondary,
          icon: null
        };
    }
  };

  if (loading) {
    return <LoadingProgress loading={loading} />;
  }

  if (error) {
    return (
      <Box sx={{ p: { xs: 2, sm: 3 } }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (!procurement) {
    return null;
  }

  const statusInfo = getStatusColor(procurement.status);

  return (
    <Box sx={{ p: { xs: 1, sm: 2 }, bgcolor: '#ffffff', minHeight: 'auto' }}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'space-between',
          alignItems: { xs: 'flex-start', sm: 'center' },
          gap: { xs: 2, sm: 1 },
          mb: { xs: 2, sm: 3 }
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: { xs: 1.5, sm: 2 },
            flex: 1,
            minWidth: 0,
            width: { xs: '100%', sm: 'auto' }
          }}
        >
          <IconButton
            onClick={onBack}
            sx={{
              color: colors.primary.main,
              bgcolor: colors.background.hover,
              '&:hover': {
                bgcolor: colors.primary.light + '20'
              },
              flexShrink: 0,
              width: { xs: 36, sm: 40 },
              height: { xs: 36, sm: 40 }
            }}
          >
            <ArrowBackIcon sx={{ fontSize: { xs: 18, sm: 20 } }} />
          </IconButton>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              variant="h5"
              fontWeight="bold"
              sx={{
                color: colors.text.primary,
                fontSize: { xs: '1.1rem', sm: '1.25rem', md: '1.4rem' },
                wordBreak: 'break-word',
                lineHeight: 1.3
              }}
            >
              {procurement.name}
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: colors.text.secondary,
                fontSize: { xs: '0.75rem', sm: '0.8rem' },
                mt: 0.5
              }}
            >
              ID: #{procurement.id}
            </Typography>
          </Box>
        </Box>
        {/* <Box sx={{ display: 'flex', gap: 1.5 }}>
          <Button
            variant="outlined"
            startIcon={<CloseIcon />}
            sx={{
              borderColor: colors.error.main,
              color: colors.error.main,
              textTransform: 'none',
              fontWeight: 600,
              borderRadius: 2,
              px: { xs: 1.5, sm: 2 },
              py: { xs: 0.5, sm: 0.75 },
              fontSize: { xs: '0.8rem', sm: '0.875rem' },
              height: { xs: 32, sm: 36 },
              minWidth: { xs: 80, sm: 100 },
              '&:hover': {
                borderColor: colors.error.dark,
                bgcolor: colors.error.light + '20'
              }
            }}
            onClick={() => {

             }}
          >
            Reject
          </Button>
          <Button
            variant="contained"
            startIcon={<CheckIcon />}
            sx={{
              bgcolor: colors.success.main,
              color: '#fff',
              textTransform: 'none',
              fontWeight: 600,
              borderRadius: 2,
              px: { xs: 1.5, sm: 2 },
              py: { xs: 0.5, sm: 0.75 },
              fontSize: { xs: '0.8rem', sm: '0.875rem' },
              height: { xs: 32, sm: 36 },
              minWidth: { xs: 80, sm: 100 },
              '&:hover': {
                bgcolor: colors.success.dark
              }
            }}
            onClick={() => {
            }}
          >
            Approve
          </Button>
        </Box> */}
      </Box>

      <Card
        elevation={0}
        sx={{
          mb: { xs: 2, sm: 3 },
          p: { xs: 1.5, sm: 2, md: 3 },
          borderRadius: { xs: 2, sm: 3 },
          border: `1px solid ${colors.alpha.black08}`,
          width: '100%',
        }}
      >
        <Grid container spacing={2}>
          {
            [
              [
                { label: 'Name:', value: procurement.name },
                procurement.category && { label: 'Category:', value: procurement.category }
              ],
              [
                { label: 'ID:', value: `#${procurement.id}` },
                { label: 'Status:', value: procurement.status }
              ],
              [
                {
                  label: 'Total Amount:',
                  value: (
                    <Typography
                      variant="body1"
                      sx={{
                        color: colors.primary.main,
                        fontWeight: 700,
                        fontSize: { xs: '0.9rem', sm: '1rem' },
                      }}
                    >
                      {formatAmount(procurement.total_amount)}
                    </Typography>
                  )
                },
                procurement.unit_price && { label: 'Unit Price:', value: formatAmount(procurement.unit_price) }
              ],
              [
                procurement.quantity && { label: 'Quantity:', value: procurement.quantity },
                procurement.supplier && { label: 'Supplier:', value: procurement.supplier }
              ],
              [
                procurement.date_of_purchase && { label: 'Date of Purchase:', value: formatDate(procurement.date_of_purchase) },
                procurement.sub_category && { label: 'Sub Category:', value: procurement.sub_category }
              ]
            ].map((row, rowIndex) =>
              <Grid container item xs={12} spacing={2} key={rowIndex}>
                {row.filter(Boolean).map((field, colIndex) =>
                  <Grid item xs={12} sm={6} key={colIndex}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography
                        variant="body2"
                        sx={{
                          color: colors.text.secondary,
                          fontWeight: 600,
                          fontSize: { xs: '0.89rem', sm: '0.92rem' },
                          textTransform: 'capitalize',
                          letterSpacing: '0.1px',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        {field.label}
                      </Typography>
                      {typeof field.value === 'string' || typeof field.value === 'number' ? (
                        <Typography
                          variant="body1"
                          sx={{
                            color: colors.text.primary,
                            fontWeight: 500,
                            fontSize: { xs: '0.97rem', sm: '1rem' },
                            wordBreak: 'break-word',
                            overflowWrap: 'break-word',
                            ml: 0.5
                          }}
                        >
                          {field.value}
                        </Typography>
                      ) : (
                        <Box sx={{ ml: 0.5 }}>{field.value}</Box>
                      )}
                    </Box>
                  </Grid>
                )}
              </Grid>
            )
          }
        </Grid>
      </Card>

      <Card
        elevation={0}
        sx={{
          mb: { xs: 2, sm: 3 },
          p: { xs: 2, sm: 3 },
          borderRadius: 3,
          border: `1px solid ${colors.alpha.black08}`,
          maxWidth: 'fit-content'
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
          <AttachFileIcon sx={{ color: colors.primary.main, fontSize: 24 }} />
          <Typography
            variant="h6"
            sx={{
              color: colors.text.primary,
              fontWeight: 600,
              fontSize: { xs: '1rem', sm: '1.125rem' }
            }}
          >
            Documents {!documentsLoading && `(${documents.length})`}
          </Typography>
        </Box>

        {documentsLoading ? (
          <Typography
            variant="body2"
            sx={{
              color: colors.text.secondary,
              fontSize: { xs: '0.875rem', sm: '0.9375rem' }
            }}
          >
            Loading documents...
          </Typography>
        ) : documents.length > 0 ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {documents.map((doc) => (
              <Box
                key={doc.id}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  p: 1.5,
                  borderRadius: 2,
                  border: `1px solid ${colors.alpha.black08}`,
                  bgcolor: colors.background.secondary,
                  transition: 'all 0.2s',
                  cursor: 'pointer',
                  '&:hover': {
                    bgcolor: colors.background.hover,
                    borderColor: colors.primary.main
                  }
                }}
                onClick={() => window.open(doc.file_url, '_blank')}
              >
                <FileIcon sx={{ color: colors.primary.main, fontSize: 28 }} />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography
                    variant="body2"
                    sx={{
                      color: colors.text.primary,
                      fontWeight: 500,
                      fontSize: { xs: '0.875rem', sm: '0.9375rem' },
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    {doc.name}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      color: colors.text.secondary,
                      fontSize: { xs: '0.7rem', sm: '0.75rem' }
                    }}
                  >
                    Click to view
                  </Typography> 
                </Box>
              </Box>
            ))}
          </Box>
        ) : (
          <Typography
            variant="body2"
            sx={{
              color: colors.text.secondary,
              fontSize: { xs: '0.875rem', sm: '0.9375rem' }
            }}
          >
            No documents available
          </Typography>
        )}
      </Card>

      {procurement.description && (
        <Card
          elevation={0}
          sx={{
            mb: { xs: 2, sm: 3 },
            p: { xs: 2, sm: 2.5 },
            borderRadius: 3,
            border: `1px solid ${colors.alpha.black08}`
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
            <DescriptionIcon sx={{ color: colors.text.secondary, fontSize: 20, mt: 0.5 }} />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                variant="caption"
                sx={{
                  color: colors.text.secondary,
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  letterSpacing: '0.3px',
                  fontSize: '0.65rem',
                  mb: 1,
                  display: 'block'
                }}
              >
                Description
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: colors.text.primary,
                  fontSize: { xs: '0.875rem', sm: '0.95rem' },
                  lineHeight: 1.6
                }}
              >
                {procurement.description}
              </Typography>
            </Box>
          </Box>
        </Card>
      )}

      {/* {procurement.remarks && (
        <Card
          elevation={0}
          sx={{
            mb: { xs: 2, sm: 3 },
            p: { xs: 2, sm: 2.5 },
            borderRadius: 3,
            border: `1px solid ${colors.alpha.black08}`,
            bgcolor: colors.error.light + '10'
          }}
        >
          <Typography
            variant="caption"
            sx={{
              color: colors.text.secondary,
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: '0.3px',
              fontSize: '0.65rem',
              mb: 1,
              display: 'block'
            }}
          >
            Remarks
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: colors.text.primary,
              fontSize: { xs: '0.875rem', sm: '0.95rem' },
              lineHeight: 1.6
            }}
          >
            {procurement.remarks}
          </Typography>
        </Card>
      )} */}

      {/* People Involved */}
      {/* <Grid container spacing={{ xs: 2, sm: 2.5 }}>
        {procurement.requester && (
          <Grid item xs={12} sm={6}>
            <Card
              elevation={0}
              sx={{
                p: { xs: 2, sm: 2.5 },
                borderRadius: 3,
                border: `1px solid ${colors.alpha.black08}`,
                height: '100%'
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  color: colors.text.secondary,
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  letterSpacing: '0.3px',
                  fontSize: '0.65rem',
                  mb: 2,
                  display: 'block'
                }}
              >
                Requested By
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar
                  src={procurement.requester.profile_image}
                  sx={{
                    width: { xs: 48, sm: 56 },
                    height: { xs: 48, sm: 56 },
                    bgcolor: colors.primary.main
                  }}
                >
                  <PersonIcon />
                </Avatar>
                <Box>
                  <Typography
                    variant="body1"
                    sx={{
                      color: colors.text.primary,
                      fontWeight: 600,
                      fontSize: { xs: '0.875rem', sm: '0.95rem' }
                    }}
                  >
                    {procurement.requester.name}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: colors.text.secondary,
                      fontSize: { xs: '0.75rem', sm: '0.8rem' }
                    }}
                  >
                    +{procurement.requester.country_code} {procurement.requester.mobile_number}
                  </Typography>
                </Box>
              </Box>
            </Card>
          </Grid>
        )}

        {procurement.approver && (
          <Grid item xs={12} sm={6}>
            <Card
              elevation={0}
              sx={{
                p: { xs: 2, sm: 2.5 },
                borderRadius: 3,
                border: `1px solid ${colors.alpha.black08}`,
                height: '100%'
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  color: colors.text.secondary,
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  letterSpacing: '0.3px',
                  fontSize: '0.65rem',
                  mb: 2,
                  display: 'block'
                }}
              >
                {procurement.status === 'approved' ? 'Approved By' : 'Reviewed By'}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar
                  src={procurement.approver.profile_image}
                  sx={{
                    width: { xs: 48, sm: 56 },
                    height: { xs: 48, sm: 56 },
                    bgcolor: colors.primary.main
                  }}
                >
                  <PersonIcon />
                </Avatar>
                <Box>
                  <Typography
                    variant="body1"
                    sx={{
                      color: colors.text.primary,
                      fontWeight: 600,
                      fontSize: { xs: '0.875rem', sm: '0.95rem' }
                    }}
                  >
                    {procurement.approver.name}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: colors.text.secondary,
                      fontSize: { xs: '0.75rem', sm: '0.8rem' }
                    }}
                  >
                    +{procurement.approver.country_code} {procurement.approver.mobile_number}
                  </Typography>
                  {procurement.approved_at && (
                    <Typography
                      variant="caption"
                      sx={{
                        color: colors.text.secondary,
                        fontSize: { xs: '0.7rem', sm: '0.75rem' },
                        display: 'block',
                        mt: 0.5
                      }}
                    >
                      {formatDate(procurement.approved_at)}
                    </Typography>
                  )}
                </Box>
              </Box>
            </Card>
          </Grid>
        )}
      </Grid> */}
    </Box>
  );
};

export default ProcurementDetails;
